// A PBX host's network interfaces, and which GDMS SIP Server each one is.
//
// Kevin, 2026-08-10: "in GDMS there is a SIP Server which has to match the PBX Host. Today
// the PBX host only has the single URL but it actually can have multiple interfaces. In this
// case the SIP server is a different interface due to how the DP750 is installed."
//
// PBX-02 answers on its public address, on the FWA segment (10.31.0.11) and on fibre. A
// handset registers to whichever it can reach from where it is installed; give it the wrong
// one and it never holds a registration, and inbound calls fall to voicemail with nothing on
// screen saying why. Verso used to hold ONE pinned GDMS id per host, which cannot express
// that at all.
//
// THE MATCH IS THE POINT. An interface with no GDMS record matched is not a cosmetic gap —
// it is the state that produced `31014 Different servers can not correspond to the same
// profile` when a second line landed on the DP750. So an unmatched interface is called out
// in amber rather than left blank, and the picker offers what GDMS actually has rather than
// asking anybody to type an id.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

// WHAT AN INTERFACE ACCEPTS. Kevin, 2026-09-04: "part of a PBX Host Interface configuration
// should be transport supported. options would be UDP, TCP, Both."
//
// `sip_servers.transport` has existed since migration 0001 with DEFAULT 'udp', and no UI ever set
// it — so every interface in the fleet claims udp regardless of what its host actually listens on,
// and the value was never worth reading. This is the form half of making it mean something.
//
// MIRRORS the allow-list in cloud/workers/src/routes/pbx-hosts.js — the worker rejects anything
// else, so a value added here alone is refused on save.
export const TRANSPORTS = [
  { value: 'udp',  label: 'UDP' },
  { value: 'tcp',  label: 'TCP' },
  { value: 'both', label: 'Both (UDP + TCP)' },
];

function transportLabel(v) {
  return TRANSPORTS.find((t) => t.value === String(v).toLowerCase())?.label ?? String(v).toUpperCase();
}

export default function PbxHostInterfaces({ agentId, canEdit }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);   // interface id, or 'new'
  const [form, setForm] = useState({
    label: '', hostname: '', gdmsSipServerId: '', isDefault: false, segmentId: '', transport: 'udp',
  });

  const q = useQuery({
    queryKey: ['pbx-host', agentId, 'interfaces'],
    queryFn: () => api.get(`/pbx-hosts/${agentId}/interfaces`),
  });

  // The networks this org runs. An interface serves one of them; a device sits on one; the
  // two meeting is what decides where a phone registers.
  const segQ = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get('/segments'),
  });

  // Creating the GDMS record itself, for a host that has none. Kept apart from `save` because it
  // writes to GDMS as well as Verso, and a failure on either half means something different.
  const [creatingGdms, setCreatingGdms] = useState(false);
  const [gdmsForm, setGdmsForm] = useState({ serverName: '', sipServer: '', transport: 'udp', segmentId: '', isDefault: true });
  const createGdms = useMutation({
    mutationFn: (bodyIn) => api.post(`/pbx-hosts/${agentId}/gdms-sip-server`, bodyIn),
    onSuccess: (data) => {
      // A PARTIAL WRITE KEEPS THE PANEL OPEN. GDMS can accept the record while the Verso interface
      // is refused (a segment already taken, say), and the route reports that as a `warning` on a
      // 2xx. Closing regardless made the panel vanish with the warning inside it, which reads as
      // clean success — and the natural next move, clicking Create again, would make a SECOND
      // GDMS record. Caught by a test, not by looking at it.
      if (!data?.warning) setCreatingGdms(false);
      qc.invalidateQueries({ queryKey: ['pbx-host', agentId, 'interfaces'] });
      // The routing banner on the parent page reads the GDMS pin, so its verdict has changed.
      qc.invalidateQueries({ queryKey: ['pbx-host-verify', agentId] });
      qc.invalidateQueries({ queryKey: ['line'] });
    },
  });

  const save = useMutation({
    mutationFn: ({ id, body }) => (id && id !== 'new'
      ? api.put(`/pbx-hosts/${agentId}/interfaces/${id}`, body)
      : api.post(`/pbx-hosts/${agentId}/interfaces`, body)),
    onSuccess: () => {
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['pbx-host', agentId, 'interfaces'] });
      // The device push resolves through these, so anything showing a line's registration
      // may now read differently.
      qc.invalidateQueries({ queryKey: ['line'] });
    },
  });

  const interfaces = q.data?.interfaces || [];
  const catalog = q.data?.gdms_catalog || [];
  const legacyPin = q.data?.legacy_pinned_gdms_sip_server_id ?? null;
  const suggestion = q.data?.gdms_suggestion || null;
  // A host nothing can register to: no interface carries a GDMS record and there is no legacy pin
  // either. This is pbx-04 on the day it enrolled, and the only control the page offered was a
  // Re-check of a verdict that could never change on its own.
  const needsGdms = !legacyPin && !interfaces.some((i) => i.gdmsSipServerId != null);

  const openGdms = () => {
    createGdms.reset();
    setGdmsForm({
      serverName: suggestion?.server_name || '',
      sipServer: suggestion?.sip_server || '',
      transport: 'udp',
      segmentId: '',
      isDefault: true,
    });
    setCreatingGdms(true);
  };

  const open = (iface) => {
    save.reset();
    setEditing(iface?.id || 'new');
    setForm({
      label: iface?.label || '',
      hostname: iface?.hostname || '',
      gdmsSipServerId: iface?.gdmsSipServerId != null ? String(iface.gdmsSipServerId) : '',
      isDefault: Boolean(iface?.isDefault),
      segmentId: iface?.segmentId != null ? String(iface.segmentId) : '',
      // The column has always existed and defaulted to 'udp'; nothing ever set it, so every row
      // in the fleet says udp whether or not that is what the host listens on.
      transport: iface?.transport || 'udp',
    });
  };

  const gdmsName = (id) => {
    const hit = catalog.find((s) => String(s.id) === String(id));
    if (!hit) return `#${id}`;
    return `${hit.name || `#${hit.id}`}${hit.address ? ` · ${hit.address}` : ''}`;
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Network interfaces</h2>
          <p className="mt-1 text-sm text-gray-600">
            The addresses this PBX answers on. A phone registers to the one it can reach from
            where it is installed, and GDMS holds a SIP Server record per interface that has
            to match.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => open(null)}
            className="flex-shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Add an interface
          </button>
        )}
      </div>

      {q.isLoading && <p className="mt-3 text-sm text-gray-500">Loading interfaces…</p>}
      {q.error && (
        <p className="mt-3 text-sm text-red-700">
          Could not load interfaces: {q.error.body?.error || q.error.message}
        </p>
      )}

      {!q.isLoading && !q.error && (<>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4">Interface</th>
                <th className="py-2 pr-4">Address</th>
                <th className="py-2 pr-4">Segment</th>
                <th className="py-2 pr-4">GDMS SIP Server</th>
                <th className="py-2 pr-4">Default</th>
                <th className="py-2 pr-2"><span className="sr-only">Edit</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {interfaces.map((i) => (
                <tr key={i.id} className="align-top">
                  <td className="py-2.5 pr-4 font-medium text-gray-900">{i.label}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs text-gray-700">
                    {i.hostname}{i.port && i.port !== 5060 ? `:${i.port}` : ''}
                    {i.transport ? <span className="ml-1.5 text-xs text-gray-500">{transportLabel(i.transport)}</span> : null}
                  </td>
                  <td className="py-2.5 pr-4">
                    {i.segmentName
                      ? <span className="text-gray-700">{i.segmentName}</span>
                      // Not an error: an interface with no segment simply is not reachable
                      // BY segment, and only serves as this host's default.
                      : <span className="text-xs text-gray-400">no segment</span>}
                  </td>
                  <td className="py-2.5 pr-4">
                    {i.gdmsSipServerId != null ? (
                      <span className="text-gray-700">{gdmsName(i.gdmsSipServerId)}</span>
                    ) : (
                      // Not decoration: a device pushed against an unmatched interface is
                      // what threw 31014.
                      <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
                        not matched — phones here cannot be provisioned
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4">
                    {i.isDefault
                      ? <span className="text-xs font-medium text-gray-700">default</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-2.5 pr-2 text-right">
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => open(i)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!interfaces.length && (
                <tr>
                  <td colSpan={6} className="py-3 text-sm text-gray-500">
                    No interfaces recorded for this host yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* NOTHING CAN REGISTER TO THIS HOST, and this is the control that fixes it.
            Kevin, 2026-09-04: "verso should be able to create the required SIP server for pbx-04."
            The values are proposed from the records GDMS already has — PBX-01 is
            pbx-01.lyonscomm.com, PBX-02 is pbx-02.lyonscomm.com — and shown for approval rather
            than written silently, because an address that does not resolve gives phones that can
            never register and a failure indistinguishable from a dozen others. */}
        {needsGdms && canEdit && !creatingGdms && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-medium text-amber-900">
              This host has no GDMS SIP Server, so no phone can be pointed at it.
            </p>
            <p className="mt-1 text-xs text-amber-800">
              GDMS needs a record whose address matches one of this host&apos;s interfaces before a
              device push can succeed. Verso can create it and match the interface in one step.
            </p>
            <button
              type="button"
              onClick={openGdms}
              className="mt-2 cursor-pointer rounded-md bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800"
            >
              Create the GDMS SIP Server
            </button>
          </div>
        )}

        {creatingGdms && (
          <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold text-gray-900">Create this host&apos;s GDMS SIP Server</h4>
            <p className="mt-0.5 text-xs text-gray-500">
              Writes the record to GDMS, then records the matching interface here. Check the address
              first — it is what every phone on this host will be told to register to.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="gdms-name" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Server name
                </label>
                <input
                  id="gdms-name"
                  value={gdmsForm.serverName}
                  onChange={(e) => setGdmsForm((f) => ({ ...f, serverName: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">The label in the GDMS console.</p>
              </div>
              <div>
                <label htmlFor="gdms-addr" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  SIP server address
                </label>
                <input
                  id="gdms-addr"
                  value={gdmsForm.sipServer}
                  onChange={(e) => setGdmsForm((f) => ({ ...f, sipServer: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
                  placeholder="pbx-04.example.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {suggestion?.sip_server
                    ? 'Suggested from this org\u2019s existing GDMS records — confirm it resolves.'
                    : 'No suggestion available: the existing records gave no naming convention to follow.'}
                </p>
              </div>
              <div>
                <label htmlFor="gdms-transport" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Transport
                </label>
                <select
                  id="gdms-transport"
                  value={gdmsForm.transport}
                  onChange={(e) => setGdmsForm((f) => ({ ...f, transport: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {TRANSPORTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="gdms-segment" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Segment
                </label>
                <select
                  id="gdms-segment"
                  value={gdmsForm.segmentId}
                  onChange={(e) => setGdmsForm((f) => ({ ...f, segmentId: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">No segment</option>
                  {(segQ.data?.segments || []).map((sg) => (
                    <option key={sg.id} value={sg.id}>{sg.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {createGdms.isError && (
              <p className="mt-3 text-sm text-red-700">
                {createGdms.error.body?.error || createGdms.error.message}
              </p>
            )}
            {createGdms.data?.warning && (
              <p className="mt-3 text-sm text-amber-800">{createGdms.data.warning}</p>
            )}

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                disabled={!gdmsForm.serverName.trim() || !gdmsForm.sipServer.trim() || createGdms.isPending}
                onClick={() => createGdms.mutate({
                  server_name: gdmsForm.serverName.trim(),
                  sip_server: gdmsForm.sipServer.trim(),
                  transport: gdmsForm.transport,
                  segment_id: gdmsForm.segmentId === '' ? null : Number(gdmsForm.segmentId),
                  is_default: gdmsForm.isDefault,
                  create_interface: true,
                })}
                className="cursor-pointer rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createGdms.isPending ? 'Creating in GDMS…' : 'Create in GDMS'}
              </button>
              <button
                type="button"
                onClick={() => { setCreatingGdms(false); createGdms.reset(); }}
                className="cursor-pointer rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </>)}

      {q.data?.gdms_catalog_error && (
        // "Couldn't ask GDMS" and "GDMS has no servers" are different, and only one of them
        // means the operator should go and create one.
        <p className="mt-3 text-xs text-amber-800">
          Couldn’t read the GDMS SIP Server list, so the picker below is empty because we
          could not ask — not because GDMS has none: {q.data.gdms_catalog_error}
        </p>
      )}

      {legacyPin != null && !interfaces.some((i) => String(i.gdmsSipServerId) === String(legacyPin)) && (
        <p className="mt-3 text-xs text-gray-500">
          This host still carries an older per-host pin ({gdmsName(legacyPin)}), used only when
          no interface matches. Matching the interfaces above replaces it.
        </p>
      )}

      {editing && (
        <div className="mt-4 rounded-md border border-blue-200 bg-blue-50/40 p-4">
          <h3 className="text-sm font-semibold text-gray-900">
            {editing === 'new' ? 'Add an interface' : 'Edit interface'}
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="iface-label" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                Name
              </label>
              <input
                id="iface-label"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. FWA, Public, Fibre"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="iface-host" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                Address
              </label>
              <p className="mb-1 mt-0.5 text-xs text-gray-500">
                Where phones send SIP — an IP for a private segment, a name for a public one.
                Taken from the GDMS record below, since that is what the phone is handed.
              </p>
              <input
                id="iface-host"
                value={form.hostname}
                onChange={(e) => setForm((f) => ({ ...f, hostname: e.target.value }))}
                placeholder="fills in from the GDMS record"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="iface-segment" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                Segment
              </label>
              <select
                id="iface-segment"
                value={form.segmentId}
                onChange={(e) => setForm((f) => ({ ...f, segmentId: e.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">No segment</option>
                {(segQ.data?.segments || []).map((sg) => (
                  <option key={sg.id} value={sg.id}>{sg.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                The network a phone must be on to reach this address. A device on the same
                segment gets this interface — which is why it is chosen here and not read
                off the name above: renaming an interface must not move any phones.
              </p>
            </div>
            <div>
              <label htmlFor="iface-transport" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                Transport
              </label>
              <select
                id="iface-transport"
                value={form.transport}
                onChange={(e) => setForm((f) => ({ ...f, transport: e.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {TRANSPORTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                What this address accepts. A phone configured for TCP against a UDP-only interface
                never registers, and the failure looks identical to a wrong address.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="iface-gdms" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                GDMS SIP Server
              </label>
              <select
                id="iface-gdms"
                value={form.gdmsSipServerId}
                onChange={(e) => {
                  const id = e.target.value;
                  const hit = catalog.find((s) => String(s.id) === String(id));
                  // The address is GDMS's, so picking the record enters it. Only ever fills a
                  // blank — an address typed before the GDMS record existed is not overwritten.
                  setForm((f) => ({
                    ...f,
                    gdmsSipServerId: id,
                    hostname: f.hostname.trim() ? f.hostname : (hit?.address || ''),
                  }));
                }}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Not matched</option>
                {catalog.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || `#${s.id}`}{s.address ? ` · ${s.address}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                The record GDMS pushes to a phone on this interface. It has to be the one whose
                address matches, or the phone never registers.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
              />
              Use this interface when an address does not say which one
            </label>
          </div>

          {save.isError && (
            <p className="mt-3 text-sm text-red-700">{save.error.body?.error || save.error.message}</p>
          )}

          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              disabled={(!form.hostname.trim() && !form.gdmsSipServerId) || save.isPending}
              onClick={() => save.mutate({
                id: editing,
                body: {
                  segment_id: form.segmentId === '' ? null : Number(form.segmentId),
                  label: form.label,
                  hostname: form.hostname,
                  gdms_sip_server_id: form.gdmsSipServerId === '' ? null : Number(form.gdmsSipServerId),
                  is_default: form.isDefault,
                  transport: form.transport,
                },
              })}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(null); save.reset(); }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
